/**
 * analytics.ts — Client-side event helpers
 *
 * Pushes page_view and custom events to:
 *   1. GTM dataLayer (legacy, still consumed by some tags)
 *   2. PostHog (product analytics)
 *   3. GA4 via server-side Measurement Protocol (/api/collect)
 *
 * Every event is automatically enriched with last-touch attribution
 * (utm_source, utm_medium, gclid, fbclid, …) from src/lib/attribution/capture.ts.
 * GA4 attribution enrichment happens server-side in /api/collect.
 */

import { isProductionAnalyticsHost } from '@config/public';
import { loadAttribution } from '@/lib/attribution/capture';
import { hasAnalyticsConsent } from '@/lib/consent/read';
import { loadSeoCtaAssignment, seoCtaEventProperties } from '@/lib/seo/cta-experiment';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    posthog?: {
      __loaded?: boolean;
      capture?: (eventName: string, properties?: Record<string, unknown>) => void;
    };
    // Buffer for conversion events fired before posthog-js finishes its
    // deferred (idle/first-interaction) boot. PostHogProvider drains this on
    // load — see capturePosthog() below.
    __bizrnrPosthogQueue?: Array<{ event: string; properties?: Record<string, unknown> }>;
  }
}

/** Cap the pre-load buffer so a session where posthog-js never boots (consent
 *  denied, SDK blocked by an ad-blocker) can't grow it without bound. */
const POSTHOG_QUEUE_MAX = 100;

// Conversion events matching GA4 conversion tracking config
export const CONVERSION_EVENTS = {
  PAGE_VIEW_REGISTER: 'page_view_register',
  PAGE_VIEW_PRICING: 'page_view_pricing',
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',
  REGISTRATION_COMPLETED: 'registration_completed',
  AUTH_METHOD_SELECTED: 'auth_method_selected',
  EMAIL_VERIFIED: 'email_verified',
  PHONE_VERIFIED: 'phone_verified',
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  PHONE_PROVISIONED: 'phone_provisioned',
  FIRST_VALUE_SEEN: 'first_value_seen',
  CHECKOUT_STARTED: 'checkout_started',
  SUBSCRIPTION_STARTED: 'subscription_started',
  CHECKOUT_VIEWED: 'checkout_viewed',
  PURCHASE: 'purchase',
  UPGRADE_CTA_CLICKED: 'upgrade_cta_clicked',
  FAILED_SUBSCRIPTION: 'failed_subscription',
  DEMO_BOOKED: 'demo_booked',
  DEMO_COMPLETED: 'demo_completed',
  DEMO_CTA_CLICKED: 'demo_cta_clicked',
  SEO_CTA_CLICKED: 'seo_cta_clicked',
  OTP_SENT: 'otp_sent',
  OTP_VERIFIED: 'otp_verified',
  OTP_FAILED: 'otp_failed',
  OTP_RESENT: 'otp_resent',
  OTP_CHANNEL_SWITCH: 'otp_channel_switch',
  LOGIN_STARTED: 'login_started',
  LOGIN_COMPLETED: 'login_completed',
  LOGIN_FAILED: 'login_failed',
} as const;

/** Events the client gtag path is allowed to fire (page views + conversions).
 *  Used by pushEvent() to decide gtag-vs-MP — NOT the Measurement Protocol allowlist. */
const GA4_FORWARDED_EVENTS = new Set<string>([
  'page_view',
  'session_start',
  'user_engagement',
  ...Object.values(CONVERSION_EVENTS),
]);

/**
 * GA4 Path B.2 — dual-pipeline reconciliation. gtag.js is the source of truth for
 * page_view / session_start / user_engagement; the server-side Measurement Protocol
 * relay is restricted to server-authoritative CONVERSIONS keyed on the stored
 * ga4_client_id. This stops the two streams from competing (double-counted sessions /
 * inflated page_view) by giving each pipeline a single, non-overlapping job.
 */
export const GA4_MP_CONVERSION_EVENTS = new Set<string>(Object.values(CONVERSION_EVENTS));

// ── Dedup guard for page_view ─────────────────────────────────────────
// The inline layout beacon already fires page_view on initial load.
// This dedup guard prevents a duplicate page_view for the same path.
let _lastBeaconPath = '';

/** Prefix attribution keys for readability in GTM (lt_source, lt_gclid, etc.). */
function enrichWithAttribution(params?: Record<string, unknown>): Record<string, unknown> {
  const snap = loadAttribution();
  const lt = snap.last_touch;
  const ft = snap.first_touch;
  const out: Record<string, unknown> = {
    ...seoCtaEventProperties(loadSeoCtaAssignment()),
    ...(params || {}),
  };
  if (lt) {
    out.lt_source = lt.source;
    out.lt_medium = lt.medium;
    out.lt_campaign = lt.campaign;
    out.lt_gclid = lt.gclid;
    out.lt_fbclid = lt.fbclid;
    out.lt_referrer = lt.referrer;
    out.lt_landing_page = lt.landing_page;
  }
  if (ft) {
    out.ft_source = ft.source;
    out.ft_medium = ft.medium;
    out.ft_campaign = ft.campaign;
    out.ft_gclid = ft.gclid;
    out.ft_fbclid = ft.fbclid;
  }
  return out;
}

/**
 * Send an event to GA4 via the server-side Measurement Protocol relay.
 * Attribution enrichment happens server-side in /api/collect (reads bizrnr_attrib cookie).
 * Only forwards events in GA4_FORWARDED_EVENTS to stay under the 500-event limit.
 */
export function beaconToGA4(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  // GA4 host guard: the server-side MP relay reaches the prod property regardless of
  // gtag, so it must be gated by host too — otherwise staging / Vercel preview / CI
  // beacons pollute prod GA4. Mirrors GoogleAnalyticsIdleLoader's gtag guard.
  if (!isProductionAnalyticsHost(window.location.hostname)) return;
  // GA4-B2: the MP relay only carries server-authoritative conversions. page_view /
  // session_start / user_engagement are owned by gtag.js and must not be duplicated here.
  if (!GA4_MP_CONVERSION_EVENTS.has(eventName)) return;

  // GA4-B3: the server-side Measurement Protocol path must honor analytics consent the
  // same way gtag/PostHog do. No-op unless analytics consent is granted — true for an
  // explicit accept OR the region-implied 'analytics' default (GA4-B1); an explicit
  // 'denied' (or unchosen) reads false.
  if (!hasAnalyticsConsent()) return;

  // Read the GA4 client ID from the same cookie the layout.tsx beacon sets
  const cidMatch = document.cookie.match(/_ga_cid=([^;]+)/);
  if (!cidMatch) return;
  const clientId = cidMatch[1];

  // Read session ID — if missing, this is a new session
  const sidMatch = document.cookie.match(/_ga_sid=([^;]+)/);
  const sessionId = sidMatch ? sidMatch[1] : String(Date.now());

  // Build GA4-safe params (only string/number/boolean — MP rejects objects)
  const ga4Params: Record<string, string | number | boolean> = {
    session_id: sessionId,
    engagement_time_msec: '100',
    // GA4-A1: MP events must carry page_location/page_referrer, otherwise GA4
    // derives the session's landing page as "(not set)".
    page_location: window.location.href,
    page_referrer: document.referrer,
  };
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
        ga4Params[k] = v;
      }
    }
  }

  // GA4-B2: do NOT synthesize session_start here — gtag.js owns session_start (and
  // user_engagement) natively. The MP relay sends only the conversion event itself.
  const events: Array<{ name: string; params: Record<string, string | number | boolean> }> = [
    { name: eventName, params: ga4Params },
  ];

  const payload = JSON.stringify({ clientId, events });

  // Best-effort: sendBeacon first, fetch keepalive fallback
  if (navigator.sendBeacon) {
    const sent = navigator.sendBeacon('/api/collect', payload);
    if (sent) return;
  }
  fetch('/api/collect', {
    method: 'POST',
    body: payload,
    keepalive: true,
  }).catch(() => {});
}

export function pushPageView(path: string, title?: string) {
  if (typeof window === 'undefined') return;

  // gtag.js handles page_view natively (including SPA route changes via
  // its History API listener). We push to dataLayer for any custom GTM
  // tags that still listen on page_view, and fire gtag event for SPA nav.
  if (window.dataLayer) {
    window.dataLayer.push(enrichWithAttribution({
      event: 'page_view',
      page_path: path,
      page_title: title || document.title,
    }));
  }

  // For SPA navigations, tell gtag to fire a page_view with the new path.
  // This ensures GA4 captures client-side route changes.
  if (path !== _lastBeaconPath && typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_location: window.location.origin + path,
      page_title: title || document.title,
    });
    _lastBeaconPath = path;
  }
}

/**
 * Deliver a client event to PostHog, surviving the deferred SDK boot.
 *
 * posthog-js loads lazily (idle + first-interaction; see PostHogProvider) and
 * the React SDK does not reliably expose the instance on `window.posthog`, so a
 * bare `window.posthog.__loaded` check silently dropped EVERY conversion event
 * fired during registration. Instead: capture immediately when the SDK is up,
 * otherwise buffer on `window.__bizrnrPosthogQueue`, which PostHogProvider
 * drains in its `loaded` callback. Reading from `window` keeps the posthog-js
 * bundle out of this lightweight, homepage-critical helper.
 *
 * Fires in anonymous mode too — these are aggregate funnel events with no PII.
 */
function capturePosthog(eventName: string, properties?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const ph = window.posthog;
  if (ph?.__loaded && typeof ph.capture === 'function') {
    ph.capture(eventName, properties);
    return;
  }
  const queue = (window.__bizrnrPosthogQueue ||= []);
  queue.push({ event: eventName, properties });
  if (queue.length > POSTHOG_QUEUE_MAX) queue.shift();
}

export function pushEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  const enriched = enrichWithAttribution(params);

  // GTM dataLayer
  if (window.dataLayer) {
    window.dataLayer.push({ event: eventName, ...enriched });
  }

  // PostHog — capture now if loaded, else buffer until the deferred SDK boots.
  capturePosthog(eventName, enriched);

  // GA4 — fire via gtag (client-side) for proper session/engagement association.
  // This ensures key events (conversions) get counted with correct bounce/engagement.
  // Falls back to MP relay for events not in GA4_FORWARDED_EVENTS.
  if (typeof window.gtag === 'function' && GA4_FORWARDED_EVENTS.has(eventName)) {
    const ga4Params: Record<string, string | number | boolean> = {};
    if (enriched) {
      for (const [k, v] of Object.entries(enriched)) {
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
          ga4Params[k] = v;
        }
      }
    }
    window.gtag('event', eventName, ga4Params);
  } else {
    // Fallback: GA4 via server-side Measurement Protocol
    beaconToGA4(eventName, enriched);
  }
}

/** Mark the initial page path so pushPageView doesn't duplicate the layout beacon. */
export function markInitialBeaconPath(): void {
  if (typeof window !== 'undefined') {
    _lastBeaconPath = window.location.pathname;
  }
}
